import { App, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '@stores/auth.store';
import { useStaffDirectory } from '@hooks/tenant/useStaffDirectory';
import AttendanceGrid from './components/AttendanceGrid';

const { Title, Text } = Typography;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export default function StaffAttendance() {
    const { message } = App.useApp();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const attendanceConfig = useAuthStore((s) => s.user?.attendanceConfig || null);

    const effectiveAttendanceConfig = useMemo(() => {
        if (attendanceConfig?.configured && Array.isArray(attendanceConfig?.branches) && attendanceConfig.branches.length > 0) {
            return attendanceConfig;
        }
        return {
            configured: true,
            branches: [{
                branchName: 'Main Campus',
                schedule: {
                    mon: { on: true, from: '08:00', to: '14:00' },
                    tue: { on: true, from: '08:00', to: '14:00' },
                    wed: { on: true, from: '08:00', to: '14:00' },
                    thu: { on: true, from: '08:00', to: '14:00' },
                    fri: { on: true, from: '08:00', to: '14:00' },
                    sat: { on: false, from: null, to: null },
                    sun: { on: false, from: null, to: null },
                }
            }]
        };
    }, [attendanceConfig]);

    const minMonth = useMemo(() => dayjs('2026-04-01'), []);
    const now = dayjs();
    const initial = now.isBefore(minMonth, 'month') ? minMonth : now;
    const [year, setYear] = useState(initial.year());
    const [month, setMonth] = useState(initial.month() + 1); // 1-12

    const monthKey = useMemo(() => `${year}-${String(month).padStart(2, '0')}`, [year, month]);

    const { rows, loading } = useStaffDirectory({ tenantId, pageSize: 100 });

    const yearOptions = useMemo(() => {
        const startY = minMonth.year();
        const endY = now.year(); // no future years
        const out = [];
        for (let y = startY; y <= endY; y += 1) out.push({ value: y, label: String(y) });
        return out;
    }, [minMonth, now]);

    const monthOptions = useMemo(() => {
        const currentMonth = now.month() + 1;
        const minYear = minMonth.year();
        const minM = minMonth.month() + 1;

        const startM = year === minYear ? minM : 1;
        const endM = year === now.year() ? currentMonth : 12; // no future months

        const out = [];
        for (let m = startM; m <= endM; m += 1) {
            out.push({ value: m, label: dayjs(`${year}-${String(m).padStart(2, '0')}-01`).format('MMM') });
        }
        return out;
    }, [minMonth, now, year]);

    // clamp to [Apr 2026 .. current month]
    useEffect(() => {
        const minYear = minMonth.year();
        const minM = minMonth.month() + 1;
        const currentYear = now.year();
        const currentM = now.month() + 1;

        if (year < minYear) {
            setYear(minYear);
            setMonth(minM);
            return;
        }
        if (year > currentYear) {
            setYear(currentYear);
            setMonth(currentM);
            return;
        }
        if (year === minYear && month < minM) {
            setMonth(minM);
            return;
        }
        if (year === currentYear && month > currentM) {
            setMonth(currentM);
        }
    }, [minMonth, month, now, year]);

    return (
        <Shell>
            <HeaderRow>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Staff Attendance</Title>
                    <Text type="secondary">Mark present/absent by day. Click any cell to toggle P/A. Saturday and Sunday are Holidays by default.</Text>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 120 }}>
                        <Text type="secondary">Year</Text>
                        <Select
                            value={year}
                            options={yearOptions}
                            style={{ width: '100%' }}
                            onChange={(v) => setYear(v)}
                        />
                    </div>
                    <div style={{ minWidth: 140 }}>
                        <Text type="secondary">Month</Text>
                        <Select
                            value={month}
                            options={monthOptions}
                            style={{ width: '100%' }}
                            onChange={(v) => setMonth(v)}
                        />
                    </div>
                </div>
            </HeaderRow>

            <AttendanceGrid
                tenantId={tenantId}
                scope="staff"
                monthKey={monthKey}
                attendanceConfig={effectiveAttendanceConfig}
                rows={rows}
                loading={loading}
                getRowId={(r) => r?.id || r?.uid}
                getRowName={(r) => (`${r?.firstName || ''} ${r?.lastName || ''}`.trim() || r?.email || '—')}
                getRowEmail={(r) => r?.email || r?.userEmail || ''}
                onError={(err) => message.error(err?.message || 'Attendance update failed')}
            />
        </Shell>
    );
}

